<script setup>
import { ref, computed } from "vue";
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Link, Head, usePage } from '@inertiajs/inertia-vue3';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import Buttons from "@/Components/Buttons.vue";
import CardBox from "@/Components/CardBox.vue";
import TableFilter from "@/Components/TableFilter.vue";
import CardBoxComponentEmpty from '@/Components/CardBoxComponentEmpty.vue';
import { Icon } from '@iconify/vue';

const props = defineProps({
    collaborators: [Array, Object],
    puestos: Object,
    roles: Object,
    entidades: [Object, Array],
    userParam: [Array, Object]
});

const rutaPrefijo = ref('collaborator');
const modulo = ref('Colaborador');
const modulos = ref('Colaboradores');
//const displayDefaultColumns = ref(null)

const page = usePage();
const user = computed(() => page.props.value.auth.user);
const isAdmin = computed(() => user.value.id_rol <= 4);

const mapOptions = (obj) => {
    if (!obj) return undefined;
    if (Object.keys(obj).length === 0) return undefined;
    return Object.entries(obj).map(([key, value]) => ({
        value: isNaN(key) ? key : Number(key),
        label: value
    }));
};

// Mostrar entidades en colaboradores // <-- Cuando hay mas de una empresa
const hasEntidad = computed(() => {
    if (!props.entidades) return false;
    return Array.isArray(props.entidades) ? props.entidades.length > 1 : Object.keys(props.entidades).length > 1;
});


// LISTA DE OPCIONES PARA EL LLAMADO DE CONTENIDO EN LA TABLA
// Para determinar el titulo de la columna, ejemplo label: 'Mi titulo'.
// Para establecer alineación del contenido usar el atributo, por ejemplo; align: 'center'
// Para habilitar un filtro tipo select; filterType: 'select',
// Si el contenido a mostrar ese select y booleano; isBoolean: true . Y establecer las etiquetas por ejemplo; booleanLabels: { true: 'Activo', false: 'Inactivo' },
// Si se desea establecer un filtro select con varias opciones personalizadas; filterType: 'select', filterOptions: [{ value: 1, label: 'Maestro' }, { value: 2, label: 'Administrador' }, { value: 3, label: 'Directivo' }],
// Si es requerido el contenido a mostrar tenga color, establecer en filter option el atributo de color, ejemplo; { value: 0, label: 'pendiente', color: 'red' }

// Valor por defecto en select, como ejemplo; filterDefault: '1'
// Si se requiere usar Select con buscado, establecer el atributo filterType: 'select-search'

// COMPONENTE TABLEFILTER, OPCIONES ADICIONALES
// Para desactivar el filtro de cada columna y activar un filtro general; simple-search = true
// Para activar la barra de desplazamiento superior (Habran 2 barras, arriba y abajo); top-scrollbar = true
// Para establecer las columnas que se muestran por defecto; userParam = true o :userParam="propsVue.userParam" 
// Para activar la seleccion de filas; checkable = true
// Para activar la exportacion de datos; exporteable = true
// Para activar modal de confirmación requestConfirm en #mass-actions de TableFilter / Template

const columns = computed(() => {
    const baseColumns = [
        { key: 'actions', label: 'Acciones' },
    ];

    if (isAdmin.value) {
        if (hasEntidad.value) {
            baseColumns.push({ key: 'entity', label: 'Entidad', filterType: 'select', filterOptions: mapOptions(props.entidades) });
        }
        baseColumns.push(
            //{ key: 'id', label: 'ID', align: 'center' },
            { key: 'apellidos', label: 'Apellidos', 'check': true, fieldType: 'linkobject', linkObject: '/collaborator/show/__ID__?action=view' },
            { key: 'nombres', label: 'Nombres', 'check': true },
            { key: 'num_doc', label: 'Documento', 'check': true },
            { key: 'email', label: 'E-Mail', 'check': true },
            { key: 'celular', label: 'Celular', 'check': true, fieldType: 'mobile_phone', align: 'between' },
            { key: 'id_puesto_laboral', label: 'Puesto Laboral', 'check': true, align: 'center', filterType: 'select', filterOptions: mapOptions(props.puestos) },
            //{ key: 'salario', label: 'Salario' },
            //{ key: 'tipo_jornal', label: 'Tipo Turno', filterType: 'select', filterOptions: [{ value: 0, label: '-' }, { value: 1, label: 'Completo' }, { value: 2, label: 'Parcial' }] },
            { key: 'label_turno', label: 'Turno Asignado', 'check': true },
            { key: 'status', label: 'Estado', 'check': true, filterType: 'select', isBoolean: true, booleanLabels: { true: 'Activo', false: 'Inactivo' }, align: 'center', filterDefault: '1' },
            // { key: 'id_rol', label: 'Rol de usuario', filterType: 'select', filterOptions: mapOptions(props.roles) },
            { key: 'id_rol', label: 'Rol de usuario', 'check': true, filterType: 'select-search', filterOptions: mapOptions(props.roles) },
            { key: 'user_status', label: 'Acceso Login', 'check': true, filterType: 'select', isBoolean: true, booleanLabels: { true: 'Activo', false: 'Inactivo' }, align: 'center' }
        );
    } else {
        baseColumns.push(
            { key: 'apellidos', label: 'Apellidos', 'check': true, fieldType: 'linkobject', linkObject: '/collaborator/show/__ID__?action=view' },
            { key: 'nombres', label: 'Nombres', 'check': true },
            { key: 'num_doc', label: 'Documento', 'check': true },
            { key: 'email', label: 'E-Mail', 'check': true },
            { key: 'celular', label: 'Celular', 'check': true, fieldType: 'mobile_phone' },
            { key: 'id_rol', label: 'Rol de usuario', 'check': true, filterType: 'select', filterOptions: mapOptions(props.roles) },
            { key: 'status', label: 'Estado', 'check': true, filterType: 'select', isBoolean: true, booleanLabels: { true: 'Activo', false: 'Inactivo' }, align: 'center', filterDefault: '1' },
            { key: 'user_status', label: 'Acceso Login', 'check': true, filterType: 'select', isBoolean: true, booleanLabels: { true: 'Activo', false: 'Inactivo' } }
        );
    }

    // Show Columnas default <-- Convertir un array de objetos a uno nuevo tomando pares específicos <-- Ahora se llama desde el contralador
    //displayDefaultColumns.value = Object.fromEntries(
    //baseColumns.map(item => item.check ? [item.key, item.check] : [])
    //)
    //console.log(baseColumns)

    return baseColumns;
});


//const tableColumns_collaborator = {"entity":true,"apellidos":true,"nombres":true,"num_doc":true,"email":true,"celular":true,"id_puesto_laboral":false,"label_turno":true,"status":true,"id_rol":true,"user_status":true}

</script>

<template>

    <Head title="Colaboradores" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader title="Colaboradores" subtitle="Gestión de personal administrativo, docentes y colaboradores." icon="tabler:users" color="indigo" :actionsOnBottomOnMobile="false">
                <template #actions>
                    <Buttons v-if="$page.props.auth.user.id_rol <= 3" label="Nuevo Colaborador" icon="f7:plus-app-fill" size="vuexy" hideLabelOnMobile color="blue" :href="route(rutaPrefijo + '-create')" />
                    <div v-else title="El administrador RRHH tiene permisos para registrar nuevos colaboradores">
                        <Buttons disabled label="El administrador de RRHH puede crear nuevos colaboradores" icon="tabler:info-circle" hideLabelOnMobile size="vuexysm" color="light" />
                    </div>
                </template>
            </PremiumPageHeader>
        </template>


        <div class="space-y-6">

            <!-- Glassmorphism Alert Info -->
            <div class="p-4 bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100/80 dark:border-indigo-900/30 rounded-2xl flex items-center gap-3 shadow-sm">
                <Icon icon="tabler:info-circle" class="w-5 h-5 text-indigo-500 shrink-0" />
                <p class="text-xs font-medium text-indigo-800 dark:text-indigo-300">
                    Por defecto se muestran los colaboradores en estado <strong>Activo</strong>. Utiliza los filtros de la tabla para buscar personal inactivo o filtrar por rol y puesto.
                </p>
            </div>

            <!-- Table Container Card -->
            <div class="bg-white dark:bg-slate-900 overflow-hidden border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 p-6">
                <TableFilter :columns="columns" :data="collaborators" :title="modulos" :ruta="rutaPrefijo" :userParam="props.userParam" top-scrollbar manage-columns class="dark:text-slate-350">
                    <template #cell-actions="{ item }">
                        <div class="flex flex-row gap-2 w-full justify-center">
                            <Link :href="route(rutaPrefijo + '-show', { id: item.id })" class="p-2 bg-amber-50 hover:bg-amber-550 hover:text-white text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 dark:hover:bg-amber-650 dark:hover:text-white rounded-xl transition-all shadow-sm" title="Editar">
                                <Icon icon="tabler:pencil" class="w-4 h-4" />
                            </Link>
                        </div>
                    </template>
                </TableFilter>
            </div>
        </div>

    </BreezeAuthenticatedLayout>
</template>
